import { Trash } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import SelectWithSearch from "@/components/ui/select-search";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/type/common";
import SimpleSelect from "@/components/ui/simple-select";
import { useContext } from "react";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

export interface MeasureItem {
  field: string;
  aggregation: "sum" | "avg" | "count" | "count_distinct" | "min" | "max";
  displayName?: string;
  type?: "bar" | "line" | "area" | "scatter";
  color?: string;
  yAxis: "left" | "right";
}

interface MeasureSelectorProps {
  measure: MeasureItem;
  fields: Field[];
  onDelete: () => void;
  onChange: (measure: MeasureItem) => void;
  defaultColor: string;
}

export const MeasureSelector = ({
  measure,
  fields,
  onDelete,
  onChange,
  defaultColor,
}: MeasureSelectorProps) => {
  const { config } = useContext(GraphConfigFormContext);
  const aggregationTypes = [
    { label: "Sum", value: "sum" },
    { label: "Average", value: "avg" },
    { label: "Count", value: "count" },
    { label: "Count Distinct", value: "count_distinct" },
    { label: "Minimum", value: "min" },
    { label: "Maximum", value: "max" },
  ];

  const chartTypes = [
    { label: "Bar", value: "bar" },
    { label: "Line", value: "line" },
    { label: "Area", value: "area" },
    { label: "Scatter", value: "scatter" },
  ];

  return (
    <Card className="w-full p-0">
      <CardContent className="p-4">
        <div className="flex justify-between items-center">
          <div className="flex-1 flex gap-2">
            <div className="w-3/5">
              <SelectWithSearch
                value={measure.field}
                options={fields as any}
                placeholder="Select field..."
                onChange={(value) =>
                  onChange({
                    ...measure,
                    field: value,
                    displayName:
                      fields.find((f) => f.field === value)?.label ||
                      measure.displayName ||
                      value,
                  })
                }
              />
            </div>
            <div className="flex flex-row items-center gap-2 w-2/5">
              <Select
                value={measure.aggregation}
                onValueChange={(value: any) =>
                  onChange({ ...measure, aggregation: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select aggregation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {aggregationTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <SimpleSelect
                value={measure.yAxis}
                options={[
                  { label: "Left", value: "left" },
                  { label: "Right", value: "right" },
                ]}
                onChange={(value) =>
                  onChange({ ...measure, yAxis: value as any })
                }
              />
            </div>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 ml-2"
            onClick={onDelete}
          >
            <Trash className="h-4 w-4 text-destructive" />
          </Button>
        </div>

        <Separator className="my-4" />

        <div className="flex flex-row items-center justify-between gap-4">
          <div className="flex flex-row items-center gap-2">
            <div>
              <Input
                id="displayName"
                value={measure.displayName || ""}
                onChange={(e) =>
                  onChange({ ...measure, displayName: e.target.value })
                }
                placeholder="Display name..."
              />
            </div>
            {config?.graph_options?.chartType === "composed" && (
              <div>
                <Select
                  value={measure.type || ""}
                  onValueChange={(value: any) =>
                    onChange({ ...measure, type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {chartTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const MeasuresInput = ({
  measures,
  fields,
  onChange,
}: {
  measures: MeasureItem[];
  fields: Field[];
  onChange: (measures: MeasureItem[]) => void;
}) => {
  const { config } = useContext(GraphConfigFormContext);

  const handleAddMeasure = () => {
    const newMeasure: MeasureItem = {
      field: "",
      aggregation: "count",
      yAxis: "left",
    };
    onChange([...measures, newMeasure]);
  };

  const handleDeleteMeasure = (index: number) => {
    const newMeasures = [...measures];
    newMeasures.splice(index, 1);
    onChange(newMeasures);
  };

  const handleChangeMeasure = (index: number, updatedMeasure: MeasureItem) => {
    const newMeasures = [...measures];
    newMeasures[index] = updatedMeasure;
    onChange(newMeasures);
  };

  return (
    <div className="flex flex-col gap-4">
      <Label>Measures</Label>
      {measures.map((measure, index) => (
        <MeasureSelector
          key={index}
          defaultColor={
            config?.graph_options.colors[
              index % config?.graph_options.colors.length
            ] || "#1f77b4"
          }
          measure={measure}
          fields={fields}
          onDelete={() => handleDeleteMeasure(index)}
          onChange={(updatedMeasure) =>
            handleChangeMeasure(index, updatedMeasure)
          }
        />
      ))}
      {(!["pie", "donut"].includes(config?.graph_options?.chartType || "") ||
        measures.length < 1) && (
        <Button
          variant="outline"
          className="w-full mt-2"
          onClick={handleAddMeasure}
        >
          Add Measure
        </Button>
      )}
    </div>
  );
};
